import { useEffect, useId, useMemo, useRef, useState } from "react";
type Bar = {
  open: number;
  high: number;
  low: number;
  close: number;
  volume: number;
};
function useProgress(target: number, duration: number, enabled: boolean) {
  const [value, setValue] = useState(enabled ? 0 : target);
  useEffect(() => {
    if (!enabled) {
      setValue(target);
      return;
    }
    setValue(0);
    const start = performance.now();
    let frame = 0;
    const tick = (now: number) => {
      const t = Math.min((now - start) / duration, 1);
      // Cosine easing keeps the start and finish soft without pausing at each day.
      setValue(target * (0.5 - Math.cos(Math.PI * t) / 2));
      if (t < 1) frame = requestAnimationFrame(tick);
    };
    frame = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(frame);
  }, [target, duration, enabled]);
  return enabled ? value : target;
}
export function Chart({
  past,
  future = [],
  horizon = future.length,
  candles = false,
  animate = false,
  playback = false,
}: {
  past: Bar[];
  future?: Bar[];
  horizon?: number;
  candles?: boolean;
  animate?: boolean;
  playback?: boolean;
}) {
  const id = useId().replace(/:/g, "");
  const reduced =
    typeof matchMedia !== "undefined" &&
    matchMedia("(prefers-reduced-motion: reduce)").matches;
  const pastProgress = useProgress(past.length - 1, 1800, playback && !reduced);
  const futureProgress = useProgress(
    future.length,
    2200,
    playback && animate && !reduced,
  );
  const rows = useMemo(() => {
    const base = past[0].close;
    return [...past, ...future].map((r) => ({
      ...r,
      open: (r.open / base) * 100,
      high: (r.high / base) * 100,
      low: (r.low / base) * 100,
      close: (r.close / base) * 100,
    }));
  }, [past, future]);
  const end = Math.min(
    rows.length - 1,
    future.length ? past.length - 1 + futureProgress : pastProgress,
  );
  const whole = Math.floor(end),
    fraction = end - whole;
  const visible = rows.slice(0, whole + 1);
  if (fraction > 0 && rows[whole + 1])
    visible.push({
      ...rows[whole + 1],
      close:
        rows[whole].close +
        (rows[whole + 1].close - rows[whole].close) * fraction,
    });
  // Use OHLC bounds in both modes so switching between line and candles never jumps.
  const min = Math.min(...rows.map((r) => r.low)),
    max = Math.max(...rows.map((r) => r.high));
  const range = Math.max(max - min, 2.5),
    targetLow = min - range * 0.12,
    targetHigh = max + range * 0.12;
  const [bounds, setBounds] = useState([targetLow, targetHigh]);
  const displayedBounds = useRef(bounds);
  useEffect(() => {
    if (reduced || !playback) {
      displayedBounds.current = [targetLow, targetHigh];
      setBounds(displayedBounds.current);
      return;
    }
    let frame = 0;
    const start = performance.now();
    const from = displayedBounds.current;
    const tick = (now: number) => {
      const t = Math.min((now - start) / 400, 1),
        ease = 1 - (1 - t) ** 3;
      displayedBounds.current = [
        (from?.[0] ?? targetLow) +
          (targetLow - (from?.[0] ?? targetLow)) * ease,
        (from?.[1] ?? targetHigh) +
          (targetHigh - (from?.[1] ?? targetHigh)) * ease,
      ];
      setBounds(displayedBounds.current);
      if (t < 1) frame = requestAnimationFrame(tick);
    };
    frame = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(frame);
  }, [targetLow, targetHigh, reduced, playback]);
  const [low, high] = bounds,
    width = 760,
    height = 360,
    pad = 16,
    span = width - pad * 2,
    priceBottom = candles ? height - 82 : height - 16;
  const total = past.length + Math.max(horizon, future.length),
    step = span / Math.max(total - 1, 1);
  const x = (i: number) => pad + i * step,
    y = (v: number) => pad + ((high - v) / (high - low)) * (priceBottom - pad);
  const position = (i: number) => (i === visible.length - 1 ? end : i);
  const points = (arr: Bar[], offset = 0) =>
    arr.map((r, i) => `${x(position(i + offset))},${y(r.close)}`).join(" ");
  const maxVol = Math.max(...rows.map((r) => r.volume), 1),
    last = visible.at(-1)!;
  const futureLine =
    visible.length >= past.length ? visible.slice(past.length - 1) : [];
  return (
    <svg
      className="price-chart"
      viewBox={`0 0 ${width} ${height}`}
      preserveAspectRatio={playback ? "none" : "xMidYMid meet"}
      role="img"
      aria-label={
        future.length
          ? "선택 이후 실제 거래일 수에 맞춰 이어지는 주가 차트"
          : "과거 주가와 판정 거래일만큼 남겨둔 차트"
      }
      data-past-visible={Math.min(whole + 1, past.length)}
      data-future-visible={Math.max(0, whole - past.length + 1)}
      data-total-days={total}
      data-progress={end}
    >
      <defs>
        <linearGradient id={id} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#4389fa" stopOpacity=".16" />
          <stop offset="100%" stopColor="#4389fa" stopOpacity="0" />
        </linearGradient>
      </defs>
      {[0.25, 0.5, 0.75].map((v) => (
        <line
          key={v}
          x1={pad}
          x2={width - pad}
          y1={pad + (priceBottom - pad) * v}
          y2={pad + (priceBottom - pad) * v}
          stroke="#e8e5f7"
          strokeDasharray="4 6"
          vectorEffect="non-scaling-stroke"
        />
      ))}
      {playback && (
        <line
          className="decision-marker"
          x1={x(past.length - 1)}
          x2={x(past.length - 1)}
          y1={pad}
          y2={priceBottom}
          stroke="#cdc7ea"
          strokeDasharray="4 6"
        />
      )}
      {!candles ? (
        <>
          <polygon
            points={`${pad},${priceBottom} ${points(visible)} ${x(end)},${priceBottom}`}
            fill={`url(#${id})`}
          />
          <polyline
            points={points(visible.slice(0, past.length))}
            fill="none"
            stroke="#4b8df8"
            strokeWidth="2.5"
            vectorEffect="non-scaling-stroke"
            strokeLinejoin="round"
            strokeLinecap="round"
          />
          {futureLine.length > 1 && (
            <polyline
              points={points(futureLine, past.length - 1)}
              fill="none"
              stroke={
                last.close >= rows[past.length - 1].close
                  ? "#eb6577"
                  : "#2468e8"
              }
              strokeWidth="2.5"
              vectorEffect="non-scaling-stroke"
              strokeLinejoin="round"
              strokeLinecap="round"
            />
          )}
          <g transform={`translate(${x(end)},${y(last.close)})`}>
            <circle
              className="chart-pulse"
              r="10"
              fill="#4389fa"
              opacity=".15"
            />
            <circle r="4" fill="#397eed" />
          </g>
        </>
      ) : (
        <>
          <line
            x1={pad}
            x2={width - pad}
            y1={height - 68}
            y2={height - 68}
            stroke="#e8e5f7"
          />
          {rows
            .slice(0, Math.min(Math.ceil(end) + 1, rows.length))
            .map((r, i) => {
              const reveal =
                  i === 0 ? 1 : Math.max(0, Math.min(1, end - i + 1)),
                ease = reveal * reveal * (3 - 2 * reveal);
              const color = r.close >= r.open ? "#ed6c78" : "#4389ec",
                w = Math.max(2, Math.min(15, step * 0.62)),
                openY = y(r.open);
              // Grow the actual OHLC glyph as a reveal effect, without inventing intraday prices.
              const upper =
                  openY + (y(Math.max(r.open, r.close)) - openY) * ease,
                body = Math.max(Math.abs(y(r.open) - y(r.close)) * ease, 1);
              return (
                <g key={i} className="candle-day" opacity={ease} data-day={i}>
                  <line
                    x1={x(i)}
                    x2={x(i)}
                    y1={openY + (y(r.high) - openY) * ease}
                    y2={openY + (y(r.low) - openY) * ease}
                    stroke={color}
                    strokeWidth="1"
                    vectorEffect="non-scaling-stroke"
                  />
                  <rect
                    x={x(i) - w / 2}
                    y={upper}
                    width={w}
                    height={body}
                    fill={color}
                  />
                  <rect
                    className="volume-bar"
                    x={x(i) - w / 2}
                    y={height - 8 - (r.volume / maxVol) * 48 * ease}
                    width={w}
                    height={(r.volume / maxVol) * 48 * ease}
                    fill={color}
                    opacity=".45"
                  />
                </g>
              );
            })}
        </>
      )}
    </svg>
  );
}
